import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Inline, LinkButton, Stack, Text, ValidationMessage } from '@etg/wings';
import { Tier } from '@eti/schema-types';
import { useHeightContext, useProperty, useTranslation } from '@eti/providers';
import { ContentWrapper } from '@eti/components';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { analytics } from '@eti/utils';
import type { AncillaryBundleOptionType } from '../../../../common/utils/models';
import { saveUpToAmountWithBundle, uniqueFeatures } from '../../../../utils/transformBundleData';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import EtgFaresSelection from './EtgFaresSelection';
import EtgFaresHeading from './EtgFaresHeading';
import EtgFaresDrawer from './EtgFaresDrawer';
import EtgFaresBottomContent from './EtgFaresBottomContent';

const growContainerStyles = (headerAndStepIndicatorHeight: number) => css`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - ${headerAndStepIndicatorHeight}px);
`;

const bottomContentContainerStyles = css`
  align-items: center;
  background-color: ${etiColors.white};
  bottom: 0;
  display: flex;
  height: 84px;
  margin-top: auto;
  position: sticky;
`;

const sectionWrapperStyles = css`
  margin-bottom: 24px;
  margin-top: 10px;

  @media ${mediaQueries.medium.up} {
    margin-top: 24px;
  }
`;

export const linkButtonStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;

  li {
    display: inline;
  }
`;

export interface ProductBundleProps {
  discountPercentage: number;
  handleProceedButtonClick: () => {};
  onSkipStepClick: () => {};
  name: string;
  numberOfTravelers: number;
  onChange: (selectedOption: AncillaryBundleOptionType) => void;
  options: AncillaryBundleOptionType[];
  selectedBundleId?: string;
}

const stepIndicatorHeight = 45;

const EtgFares = ({
  discountPercentage,
  handleProceedButtonClick,
  onSkipStepClick,
  name,
  numberOfTravelers,
  onChange,
  options,
  selectedBundleId,
}: ProductBundleProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { headerHeightContext } = useHeightContext();
  const headerAndStepIndicatorHeight = headerHeightContext + stepIndicatorHeight;
  const [hasValidationError, setHasValidationError] = useState(false);

  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const firstRadioRef = useRef<HTMLInputElement>(null);
  const validationRef = useRef<HTMLDivElement>(null);

  const etgFaresDetailsDrawerLinkText = t('Product.ETG.Fares.Details.Drawer.Link');
  const pricePerTraveler = p('Bundle.Overlay.Price.Per.Person.Enabled');
  const priceText = pricePerTraveler
    ? t('Price.Per.Person')
    : t('Product.FlexibleTicket.Bundle.Price.Text');
  analytics.useLogMouseFlowTagEffect('ETG bundle');
  const sellableOptions = options.filter((option) => option.type !== Tier.Basic);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedOptionId = event.target.value;
    const selectedOption = options.find((option) => option.id === selectedOptionId);

    if (hasValidationError) {
      setHasValidationError(false);
    }
    if (selectedOption) {
      onChange(selectedOption);
    }
  };

  useEffect(() => {
    if (hasValidationError && validationRef.current) {
      validationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        validationRef.current?.focus();
      }, 400);
    }
  }, [hasValidationError]);

  const onProceedClick = () => {
    if (!selectedBundleId) {
      setHasValidationError(true);
    } else {
      handleProceedButtonClick();
    }
  };

  const { isOpen, handleToggle } = useToggle(false);

  const focusSkipButton = () => {
    skipButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    skipButtonRef.current?.focus();
  };

  return (
    <section className={growContainerStyles(headerAndStepIndicatorHeight)}>
      <ContentWrapper className={sectionWrapperStyles}>
        <Stack spacing={24}>
          <EtgFaresHeading
            discountPercentage={discountPercentage}
            numberOfSellableOptions={sellableOptions.length}
            savedAmount={saveUpToAmountWithBundle(sellableOptions, discountPercentage)}
          />
          <EtgFaresDrawer
            dataTestId="etg-overlay-drawer"
            handleClose={handleToggle}
            isOpen={isOpen}
            sellableOptions={uniqueFeatures(sellableOptions)}
            title={etgFaresDetailsDrawerLinkText}
          />
          {hasValidationError && (
            <ValidationMessage
              aria-labelledby="etg-fares-validation-message-text"
              data-testid="etg-fares-validation-message"
              ref={validationRef}
              tabIndex={-1}
              variant="error"
            >
              <Inline spacing={8}>
                <Text aria-live="assertive" id="etg-fares-validation-message-text">
                  {t('Product.ETG.Fares.Validation.Message')}
                </Text>
                <ul className={linkButtonStyles}>
                  <li>
                    <LinkButton onClick={() => firstRadioRef.current?.focus()}>
                      {t('Product.ETG.Fares.Validation.SelectOption.Text')}
                    </LinkButton>
                  </li>
                  <li>
                    <LinkButton onClick={focusSkipButton}>
                      {t('YourSelectedTrip.Page.SkipStepButton.Label')}
                    </LinkButton>
                  </li>
                </ul>
              </Inline>
            </ValidationMessage>
          )}

          <EtgFaresSelection
            discountPercentage={discountPercentage}
            firstRadioRef={firstRadioRef}
            handleOnChange={handleOnChange}
            hasValidationError={hasValidationError}
            name={name}
            numberOfTravelers={numberOfTravelers}
            priceText={priceText}
            sellableOptions={sellableOptions}
            shouldShowPricePerPerson={pricePerTraveler}
          />
          <Inline>
            <LinkButton
              aria-controls="product-info-drawer"
              aria-expanded={isOpen ? 'true' : 'false'}
              aria-haspopup="dialog"
              data-testid={`${name}-drawer-link-content`}
              onClick={handleToggle}
            >
              {etgFaresDetailsDrawerLinkText}
            </LinkButton>
          </Inline>
        </Stack>
      </ContentWrapper>
      <div className={bottomContentContainerStyles}>
        <EtgFaresBottomContent
          onProceedClick={onProceedClick}
          onSkipStepClick={onSkipStepClick}
          skipButtonRef={skipButtonRef}
        />
      </div>
    </section>
  );
};

export default EtgFares;
